import { capitalize } from 'lodash';
import type { PlanPeriod } from '@frameio/graphql-client/generated';
import { useGetAccountUsageQuery } from '@frameio/graphql-client/generated';
import { useActiveAccountId } from '@frameio/shared/contexts';
import { EMPTY_OBJECT } from '@frameio/utils';
import { SubscriptionUpdateSource } from '../../feature-gate/config';
import { ModalType } from '../../modal/components';
import { usePlanManagementModalContext } from '../../modal/hooks';
import {
  getIncludedMemberSeatsAvailable,
  getMemberSeatSubscriptionCost,
  isMemberSeatUpgradeAllowed,
} from './accountSubscriptionDetailsUtils';
import { TIER_MAP, useNextTierPlan } from './useNextTierPlan';

const DEFAULT_SEATS_DATA = {
  seatLimit: 0,
};

export const useAccountSubscriptionDetails = ({
  isOverSeatLimit = false,
}: {
  isOverSeatLimit?: boolean;
} = EMPTY_OBJECT) => {
  const accountId = useActiveAccountId();
  const planManagementModalContext = usePlanManagementModalContext();
  const { data: accountUsage } = useGetAccountUsageQuery({
    variables: { accountId: accountId ?? '' },
    skip: !accountId,
  });

  const subscription = accountUsage?.account?.subscription;
  const isFreeTierPlan = subscription?.plan?.tier === 'FREE';
  const perUserStorageIncrement =
    subscription?.plan?.perUserStorageIncrement ?? 0;

  const calculateSubscriptionCost = () => {
    const userLimitAddOnCost = subscription?.addOns?.find(
      (addOn) => addOn.modifier === 'user_limit'
    )?.cost;

    return getMemberSeatSubscriptionCost({
      isFreeTierPlan,
      planCost: subscription?.plan?.cost,
      planUserLimit: subscription?.plan?.userLimit,
      userLimitAddOnCost,
    });
  };

  const subscriptionCost = calculateSubscriptionCost();
  const planPeriod: PlanPeriod | undefined = subscription?.plan?.period;
  const memberSeatUsage = accountUsage?.account?.usageByLicenseType?.member;
  const { seatLimit } = memberSeatUsage || DEFAULT_SEATS_DATA;
  const includedMemberSeatsAvailable = getIncludedMemberSeatsAvailable({
    planUserLimit: subscription?.plan?.userLimit,
    seatsUsed: memberSeatUsage?.seatsUsed,
  });
  const tier = subscription?.plan?.tier;

  const nextTierPlan = useNextTierPlan({
    tier,
    period: subscription?.plan?.period,
  });
  const isHardLimit = memberSeatUsage?.isHardLimit ?? false;
  const shouldAllowPlanTierUpgrade = isMemberSeatUpgradeAllowed({ tier });
  const isOverLimit = isOverSeatLimit && isHardLimit;

  return {
    availableSeats: memberSeatUsage?.availableSeats,
    includedMemberSeatsAvailable,
    isFreeTierPlan,
    isHardLimit,
    isOnTrial: subscription?.trial?.isActive ?? false,
    isOverLimit,
    isMemberSeatUpgradeAllowed: shouldAllowPlanTierUpgrade,
    isGuestSeatUpgradeAllowed: tier !== 'ENTERPRISE' && tier !== 'TEAM',
    onUpgradePlanTier:
      shouldAllowPlanTierUpgrade && nextTierPlan
        ? () => {
            planManagementModalContext?.setSelectedPlanWithFeature({
              ...nextTierPlan,
              cost: nextTierPlan.cost ?? 0,
              modalType: ModalType.UPGRADE,
              subscriptionUpdateSource:
                SubscriptionUpdateSource.MEMBER_LIMIT_BLOCK,
            });
          }
        : undefined,
    perUserStorageIncrement,
    planPeriod,
    planTier: tier,
    seatLimit,
    subscriptionCost,
    subscriptionUpgradeText: () => {
      return tier ? capitalize(TIER_MAP[tier]) : '';
    },
  };
};
